"use client"

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ExternalLink, Loader2 } from "@/lib/icons"
import type { SourceMatch } from "@/lib/reverse-search"
import { useTranslations } from "next-intl"

interface FindSourceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  loading: boolean
  matches: SourceMatch[]
  /** One message per engine that failed, so a working engine's matches are still shown */
  errors: Record<string, string>
}

const ENGINE_NAME: Record<string, string> = { iqdb: "IQDB", tracemoe: "trace.moe" }

// "Find source": the matches IQDB (lookalike booru posts) and trace.moe (anime screenshots) found for this image, best
// first. Each row opens the match on its own site. The other engines (Google Lens, Yandex, ImgOps, SauceNAO) stay as
// links in the menu, since they have no API we can read.
export function FindSourceDialog({ open, onOpenChange, loading, matches, errors }: FindSourceDialogProps) {
  const t = useTranslations("findSourceDialog")
  const failed = Object.entries(errors)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("searching")}
          </div>
        )}

        {!loading && matches.length === 0 && (
          <p className="py-4 text-sm text-muted-foreground">{errors.image ? t("blocked") : failed.length > 0 ? t("couldNotSearch") : t("none")}</p>
        )}

        {!loading && matches.length > 0 && (
          <ul className="space-y-2">
            {matches.map((match) => (
              <li key={match.url}>
                <a
                  href={match.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-md border border-border/50 p-2 transition-colors hover:bg-primary/15 focus-visible:bg-primary/15 focus-visible:outline-none"
                >
                  {match.thumbnail ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={match.thumbnail} alt="" className="h-14 w-14 shrink-0 rounded object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="h-14 w-14 shrink-0 rounded bg-muted" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{match.title || match.site}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {match.title ? `${match.site} · ` : ""}
                      {ENGINE_NAME[match.engine]}
                      {match.detail ? ` · ${match.detail}` : ""}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-semibold tabular-nums">{t("match", { value: match.similarity })}</div>
                    <ExternalLink className="ml-auto mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}

        {!loading && failed.length > 0 && matches.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {failed.map(([engine]) => ENGINE_NAME[engine] ?? engine).join(", ")} {t("partlyFailed")}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
